import React from 'react';
import {
  Badge,
  BedroomChild,
  BorderColor,
  CorporateFare,
  DateRange,
  Person,
  Send,
  Task
} from '@mui/icons-material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCrmEsignAvailability } from '@/pages/CRM/Screener/hooks/useCrmEsignAvailability';
import { useIsBedBoardEnabled } from '@/pages/CRM/Screener/hooks/useIsBedBoardEnabled';
import { useIsReferralOutboundEnabled } from '@/pages/CRM/Screener/hooks/useIsReferralOutboundEnabled';
import { useIsScreenerLeadWaitlistEnabled } from '@/pages/CRM/Screener/hooks/useIsScreenerLeadWaitlistEnabled';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import useNavigateToScreenerAllFacilities from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useNavigateToScreenerAllFacilitites';
import { RoutePath } from '@/routes/RoutePath';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

export interface SidebarItem {
  icon: React.ReactNode;
  text: string;
  path: string;
  showOnMobile?: boolean;
  onClick?: () => void;
}

export const useGetSidebarCRMItems = (): SidebarItem[] => {
  const currentUser = useCurrentUser().data!;
  const isBedBoardEnabled = useIsBedBoardEnabled();
  const isReferralOutboundEnabled = useIsReferralOutboundEnabled();
  const isWaitlistEnabled = useIsScreenerLeadWaitlistEnabled();
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const { isEnabled: isCrmEsignEnabled } = useCrmEsignAvailability();
  const navigateToScreenerAllFacilities = useNavigateToScreenerAllFacilities(
    RoutePath.CRMScreenerAllLeads
  );
  const navigateToScreenerAllFacilitiesESign =
    useNavigateToScreenerAllFacilities(RoutePath.CRMScreenerESign);
  return [
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER)
      ? [
          {
            icon: <ScreenSearchDesktopIcon />,
            text: 'Screener',
            path: RoutePath.CRMScreenerAllLeads,
            showOnMobile: true,
            onClick: isAllFacilitiesSelected
              ? navigateToScreenerAllFacilities
              : undefined
          }
        ]
      : []),
    ...(isWaitlistEnabled
      ? [
          {
            icon: <ContactPageIcon />,
            text: 'Waitlist',
            path: RoutePath.CRMWaitlist,
            showOnMobile: true
          }
        ]
      : []),
    ...(isReferralOutboundEnabled
      ? [
          {
            icon: <Send />,
            text: 'Outbound Referrals',
            path: RoutePath.CRMReferralOutbound,
            showOnMobile: true
          }
        ]
      : []),
    ...(isBedBoardEnabled
      ? [
          {
            icon: <Person />,
            text: 'Residents',
            path: RoutePath.CrmScreenerResidentsAllResidents,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() && isBedBoardEnabled
      ? [
          {
            icon: <BedroomChild />,
            text: 'Bed Board',
            path: RoutePath.CRMBedboard,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() &&
    FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_TASKS)
      ? [
          {
            icon: <DateRange />,
            text: 'Tasks',
            path: RoutePath.CRMTasks,
            showOnMobile: true
          }
        ]
      : currentUser.isScreenerCustomer()
        ? []
        : [
            {
              icon: <DateRange />,
              text: 'Tasks',
              path: RoutePath.CRMTasks,
              showOnMobile: true
            }
          ]),
    ...(isCrmEsignEnabled
      ? [
          {
            icon: <BorderColor />,
            text: 'eSign',
            path: RoutePath.CRMScreenerESign,
            showOnMobile: true,
            onClick: isAllFacilitiesSelected
              ? navigateToScreenerAllFacilitiesESign
              : undefined
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer()
      ? [
          {
            icon: <Badge />,
            text: 'Sources',
            path: RoutePath.CRMSources,
            showOnMobile: true
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(ExacareFeature.DATA_CENTER_TAB) &&
    currentUser.isDataCenterEnabled()
      ? [
          {
            icon: <Task />,
            text: 'Data Center',
            path: generatePathWithDomain(RoutePath.DataCenterDashboards)
          }
        ]
      : []),
    ...(currentUser.hasMinimumUserRole('L1')
      ? [
          {
            icon: <CorporateFare />,
            text: 'Admin Hub',
            path: generatePathWithDomain(RoutePath.AdminHub)
          }
        ]
      : [])
  ];
};
